import { useState, useCallback } from 'react';
import { STARTER_INVENTORY, EMPTY_EQUIPPED } from '../data/items';

const SAVE_KEY = 'survivor_save';

function loadSave() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

function writeSave(inventory, equipped) {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify({ inventory, equipped }));
  } catch {}
}

const INVENTORY_SIZE = 400;

/**
 * Manages the player's inventory (400 slots) and equipped items.
 * State is persisted to localStorage under 'survivor_save'.
 */
export function useInventory() {
  const [state, setState] = useState(() => {
    const saved = loadSave();
    if (saved) {
      // Guard against old save formats that might be missing the equipped object
      let inv = Array.isArray(saved.inventory) ? saved.inventory : STARTER_INVENTORY;
      // Migration: pad inventories saved before the 400-slot expansion
      if (inv.length < INVENTORY_SIZE) {
        inv = [...inv, ...Array(INVENTORY_SIZE - inv.length).fill(null)];
      }
      return {
        inventory: inv,
        equipped:  (saved.equipped && typeof saved.equipped === 'object') ? { ...EMPTY_EQUIPPED, ...saved.equipped } : EMPTY_EQUIPPED,
      };
    }
    return { inventory: STARTER_INVENTORY, equipped: EMPTY_EQUIPPED };
  });

  const { inventory, equipped } = state;

  /**
   * Equip item at invIndex.
   * Rings (slotType 'ring') try ring1 first, then ring2, displacing ring1 if both filled.
   * Any displaced item is swapped back into the inventory slot the new item came from.
   */
  const equipItem = useCallback((item, invIndex) => {
    setState(prev => {
      const newInv = [...prev.inventory];
      const newEq  = { ...prev.equipped };

      let targetSlot;
      if (item.slotType === 'ring') {
        if (!newEq.ring1)       targetSlot = 'ring1';
        else if (!newEq.ring2)  targetSlot = 'ring2';
        else                    targetSlot = 'ring1';
      } else if (item.slotType === 'rune') {
        const RUNE_SLOTS = ['helmet_rune','weapon_rune','armor_rune','ring1_rune','ring2_rune','gloves_rune','boots_rune','amulet_rune'];
        targetSlot = RUNE_SLOTS.find(s => !newEq[s]) ?? 'helmet_rune';
      } else {
        targetSlot = item.slotType;
      }

      newInv[invIndex] = newEq[targetSlot];
      newEq[targetSlot] = item;

      const next = { inventory: newInv, equipped: newEq };
      writeSave(next.inventory, next.equipped);
      return next;
    });
  }, []);

  /**
   * Unequip item from slotId and move it to the first empty inventory slot.
   * Does nothing if inventory is full.
   */
  const unequipItem = useCallback((slotId) => {
    setState(prev => {
      const item = prev.equipped[slotId];
      if (!item) return prev;

      const newInv = [...prev.inventory];
      const emptyIdx = newInv.findIndex(s => s === null);
      if (emptyIdx === -1) return prev;

      newInv[emptyIdx] = item;
      const newEq = { ...prev.equipped, [slotId]: null };
      const next = { inventory: newInv, equipped: newEq };
      writeSave(next.inventory, next.equipped);
      return next;
    });
  }, []);

  /**
   * Toggle the user-lock flag on the item at invIndex.
   * Locked items are immune to selling (individually and via sellAll).
   */
  const lockItem = useCallback((invIndex) => {
    setState(prev => {
      const item = prev.inventory[invIndex];
      if (!item) return prev;
      const newInv = [...prev.inventory];
      newInv[invIndex] = { ...item, locked: !item.locked };
      const next = { inventory: newInv, equipped: prev.equipped };
      writeSave(next.inventory, next.equipped);
      return next;
    });
  }, []);

  /**
   * Remove the item at invIndex (sell it). Returns the removed item so
   * the caller can credit gold. Returns null if the slot was empty or locked.
   * Legendary and set items are always protected from selling.
   */
  const sellItem = useCallback((invIndex) => {
    let sold = null;
    setState(prev => {
      const item = prev.inventory[invIndex];
      if (!item || item.locked || item.rarity === 'legendary' || item.rarity === 'set') return prev;
      sold = item;
      const newInv = [...prev.inventory];
      newInv[invIndex] = null;
      const next = { inventory: newInv, equipped: prev.equipped };
      writeSave(next.inventory, next.equipped);
      return next;
    });
    return sold;
  }, []);

  /**
   * Sell every non-locked inventory item matching a slot-type filter in one setState call.
   * @param {string|string[]} slotTypeFilter  'all' | slotType | array of slotType/filter ids
   * Returns the array of sold items so the caller can credit the total gold.
   */
  const sellAllItems = useCallback((slotTypeFilter = 'all') => {
    const sold = [];
    const filters = Array.isArray(slotTypeFilter) ? slotTypeFilter : [slotTypeFilter];
    const isAll = filters.includes('all');
    setState(prev => {
      const newInv = [...prev.inventory];
      for (let i = 0; i < newInv.length; i++) {
        const item = newInv[i];
        if (!item) continue;
        if (item.locked) continue; // skip user-locked items
        if (item.rarity === 'legendary' || item.rarity === 'set') continue; // always protect
        if (item.slotType === 'rune') continue; // runes have their own sell-all
        if (!isAll && !filters.includes(item.slotType)) continue;
        sold.push(item);
        newInv[i] = null;
      }
      if (sold.length === 0) return prev;
      const next = { inventory: newInv, equipped: prev.equipped };
      writeSave(next.inventory, next.equipped);
      return next;
    });
    return sold;
  }, []);

  /**
   * Sell every non-locked rune in inventory in one setState call.
   * Returns the array of sold runes so the caller can credit gold.
   */
  const sellAllRunes = useCallback(() => {
    const sold = [];
    setState(prev => {
      const newInv = [...prev.inventory];
      for (let i = 0; i < newInv.length; i++) {
        const item = newInv[i];
        if (!item) continue;
        if (item.slotType !== 'rune') continue;
        if (item.locked) continue;
        sold.push(item);
        newInv[i] = null;
      }
      if (sold.length === 0) return prev;
      const next = { inventory: newInv, equipped: prev.equipped };
      writeSave(next.inventory, next.equipped);
      return next;
    });
    return sold;
  }, []);

  /**
   * Add an array of dropped items into the first available inventory slots.
   * Returns the number of items that were successfully added.
   * Items beyond the 200-slot cap are silently skipped.
   */
  const addDroppedItems = useCallback((items) => {
    if (!items || items.length === 0) return 0;
    let added = 0;
    setState(prev => {
      const newInv = [...prev.inventory];
      for (const item of items) {
        const emptyIdx = newInv.findIndex(s => s === null);
        if (emptyIdx === -1) break; // inventory full
        // Auto-lock legendary and set items so they can't be accidentally sold
        const autoLock = item.rarity === 'legendary' || item.rarity === 'set';
        newInv[emptyIdx] = autoLock ? { ...item, locked: true } : item;
        added++;
      }
      if (added === 0) return prev;
      const next = { inventory: newInv, equipped: prev.equipped };
      writeSave(next.inventory, next.equipped);
      return next;
    });
    return added;
  }, []);

  /**
   * Apply (or replace) an enhancement on the item at invIndex.
   * @param {number} invIndex     Index in the inventory array.
   * @param {object} enhancement  { level, stats } from rollEnhancement().
   */
  const enhanceItem = useCallback((invIndex, enhancement) => {
    setState(prev => {
      const item = prev.inventory[invIndex];
      if (!item) return prev;
      const newInv = [...prev.inventory];
      newInv[invIndex] = { ...item, enhancement };
      const next = { inventory: newInv, equipped: prev.equipped };
      writeSave(next.inventory, next.equipped);
      return next;
    });
  }, []);

  /**
   * Equip all runes from inventory that are required by the given runeword,
   * skipping any already socketed. Fills the first available rune slot for each.
   * Stops early if all 8 rune slots are occupied.
   */
  const equipRuneWord = useCallback((runeWord) => {
    setState(prev => {
      const newInv = [...prev.inventory];
      const newEq  = { ...prev.equipped };
      const RUNE_SLOTS = ['helmet_rune','weapon_rune','armor_rune','ring1_rune','ring2_rune','gloves_rune','boots_rune','amulet_rune'];

      // Track which rune names are already socketed so we don't double-socket
      const socketedNames = new Set(RUNE_SLOTS.map(s => newEq[s]?.name).filter(Boolean));

      for (const runeName of runeWord.requiredRunes) {
        if (socketedNames.has(runeName)) continue; // already in a socket

        const invIdx = newInv.findIndex(item => item?.slotType === 'rune' && item.name === runeName);
        if (invIdx === -1) continue; // player doesn't own this rune

        const targetSlot = RUNE_SLOTS.find(s => !newEq[s]);
        if (!targetSlot) break; // all 8 slots occupied

        newEq[targetSlot] = newInv[invIdx];
        newInv[invIdx]    = null;
        socketedNames.add(runeName);
      }

      const next = { inventory: newInv, equipped: newEq };
      writeSave(next.inventory, next.equipped);
      return next;
    });
  }, []);

  /**
   * Unequip all 8 rune sockets back to inventory in one setState call.
   * Silently skips any rune whose slot couldn't be placed (full inventory).
   */
  const unequipAllRunes = useCallback(() => {
    setState(prev => {
      const RUNE_SLOTS = ['helmet_rune','weapon_rune','armor_rune','ring1_rune','ring2_rune','gloves_rune','boots_rune','amulet_rune'];
      const newInv = [...prev.inventory];
      const newEq  = { ...prev.equipped };
      let changed  = false;

      for (const slot of RUNE_SLOTS) {
        if (!newEq[slot]) continue;
        const emptyIdx = newInv.findIndex(s => s === null);
        if (emptyIdx === -1) break; // inventory full
        newInv[emptyIdx] = newEq[slot];
        newEq[slot]      = null;
        changed          = true;
      }

      if (!changed) return prev;
      const next = { inventory: newInv, equipped: newEq };
      writeSave(next.inventory, next.equipped);
      return next;
    });
  }, []);

  return { inventory, equipped, equipItem, unequipItem, addDroppedItems, sellItem, sellAllItems, sellAllRunes, enhanceItem, lockItem, equipRuneWord, unequipAllRunes };
}
